/**
 * Authentication utility functions
 * Handles token validation, refresh, and cleanup
 */

interface TokenPayload {
  exp: number;
  iat?: number;
  user_id?: number;
  email?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  tenant?: string;
  tenant_id?: number;
  designation?: string;
  is_staff?: boolean;
  is_superuser?: boolean;
  language_id?: number;
  language_code?: string;
  language_name?: string;
  token_type?: string;
  jti?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface TokenValidationResult {
  isValid: boolean;
  payload: TokenPayload | null;
  error?: string;
}

/**
 * Decodes JWT token payload without verification
 * Note: This only decodes - signature verification should happen server-side
 */
export const decodeToken = (token: string): TokenPayload | null => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    // Validate base64url format (only contains A-Za-z0-9_-)
    if (!/^[A-Za-z0-9_-]+$/.test(base64Url)) {
      // eslint-disable-next-line no-console
      console.error('[Auth] Invalid base64url characters in token');
      return null;
    }

    // Convert base64url to base64
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    
    // Decode base64 and handle UTF-8 characters properly
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[Auth] Token decode failed:', error);
    return null;
  }
};

/**
 * Validates token expiration and structure
 */
export const validateToken = (token: string | null): TokenValidationResult => {
  if (!token) {
    return { isValid: false, payload: null, error: 'No token provided' };
  }

  // Check JWT structure (header.payload.signature)
  if (token.split('.').length !== 3) {
    return { isValid: false, payload: null, error: 'Invalid token format' };
  }

  const payload = decodeToken(token);
  
  if (!payload) {
    return { isValid: false, payload: null, error: 'Token decode failed' };
  }

  // Validate expiration claim exists
  if (!payload.exp) {
    return { isValid: false, payload: null, error: 'Token missing expiration' };
  }

  const currentTime = Math.floor(Date.now() / 1000); // Convert to seconds
  const isExpired = currentTime >= payload.exp;

  if (isExpired) {
    return { isValid: false, payload, error: 'Token expired' };
  }

  // Add buffer time check (warn if token expires in less than 5 minutes)
  const expiresInSeconds = payload.exp - currentTime;
  if (expiresInSeconds < 300) {
    // eslint-disable-next-line no-console
    console.warn(`[Auth] Token expires in ${expiresInSeconds}s - consider refreshing`);
  }

  return { isValid: true, payload };
};

/**
 * Gets token from storage (checks both localStorage and sessionStorage)
 */
export const getStoredToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('token') || sessionStorage.getItem('token');
};

/**
 * Clears authentication data from storage
 */
export const clearAuthData = (): void => {
  if (typeof window === 'undefined') return;
  
  localStorage.removeItem('token');
  sessionStorage.removeItem('token');
  
  // Clear any other auth-related data
  localStorage.removeItem('refreshToken');
  sessionStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  sessionStorage.removeItem('user');
  
  // Clear clinic data to prevent stale clinic context across sessions/users
  localStorage.removeItem('clinicId');
};

/**
 * Check if user is currently authenticated with a valid token
 * This is a pure function that only checks authentication status without side effects
 * Callers are responsible for cleanup if needed
 * @returns {boolean} True if authenticated with valid token, false otherwise
 */
export const isAuthenticated = (): boolean => {
  const token = getStoredToken();
  if (!token) {
    return false;
  }

  const { isValid } = validateToken(token);
  return isValid;
};

/**
 * Gets time remaining until token expiration (in seconds)
 * Returns null if token is invalid or expired
 */
export const getTokenTimeRemaining = (): number | null => {
  const token = getStoredToken();
  const { isValid, payload } = validateToken(token);
  
  if (!isValid || !payload?.exp) return null;
  
  const currentTime = Math.floor(Date.now() / 1000);
  return Math.max(0, payload.exp - currentTime);
};

/**
 * Extracts user information from token
 */
export const getUserFromToken = (): Partial<TokenPayload> | null => {
  const token = getStoredToken();
  const { isValid, payload } = validateToken(token);
  
  if (!isValid || !payload) return null;
  
  return {
    user_id: payload.user_id,
    email: payload.email,
    username: payload.username,
    first_name: payload.first_name,
    last_name: payload.last_name,
    tenant: payload.tenant,
    tenant_id: payload.tenant_id,
    designation: payload.designation,
    language_code: payload.language_code,
    ...payload
  };
};
